<template lang="pug">
  .modeling
    .no-print.toolbar
      .mobile-title
        span.mobile-title__number.semibold 10
        span Администрирование
      .line
        h1.title {{$translate('Карта')}}
    .map-layer.map.absolute(ref="map" v-once)
    .legend.legend_modeling(:class="showMapLegend ? 'opened' : 'closed'" )
      e-perfect-scrollbar
        .legend__head(@click="showMapLegend = !showMapLegend")
          .legend__title {{$translate('Слои')}}
          button.reset-btn.legend__close(v-if="showMapLegend")
          button.reset-btn.legend__open(v-else)
        .legend__body
          button.reset-btn.button.button_wide.modeling__button(@click='getThreads') Рассчитать модельную схему потоков
          button.reset-btn.button.button_wide.modeling__button(@click='onShowOptimalModal') Построить оптимальный маршрут
          button.reset-btn.button.button_wide.modeling__button(
            v-if="userCan('feature-modeling-show-optimal-route')"
            @click='onShowOptimalSchema'
          ) Оптимальная схема размещения
          button.reset-btn.button.button_wide.modeling__button(@click='showOptimalZones = true') Модельные зоны РО
          .modeling__text(v-if='showOptimalText')
            div Целевая функция затрат:&nbsp;
              b {{zones.cost}}
            div Общие затраты на управление отходами:&nbsp;
              b {{zones.control}}
            div Общие затраты на сбор отходов:&nbsp;
              b {{zones.collection}}
            div Общие затраты на транспортировку отходов:&nbsp;
              b {{zones.transportation}}
            div Общие затраты на обработку отходов:&nbsp;
              b {{zones.processing}}
    date-slider(
      :mapType='selectedMappingObject'
      :dataUrl='sliderYearsUrl'
      @map-update='updateMapByYear'
      :route='$route'
    )
    optimal-modal(
      v-if="showOptimalModal"
      @close="showOptimalModal = false"
      @saveOptimalRoute="saveOptimalRoute"
    )
    optimal-schema(
      v-if="showOptimalSchema"
      :map="map"
      :year="year"
      :geoObjects="objects"
      @close="closeOptimalSchema"
      @saveOptimalSchema="saveOptimalSchema"
    )
    optimal-zones(
      v-if="showOptimalZones"
      :year="year"
      @close="showOptimalZones = false"
      @setOptimalZones='setOptimalZones'
    )
</template>
<script>
import L from 'leaflet'
import axios from 'axios'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

import measureMixin from '@/app/geo/mixins/measure-mixin'
import { REGION_CONFIG } from '@/config/regions'
import { TILE_LAYER } from '@/config/map'
import BorderDrawer from '@/utils/map/BorderDrawer'
import {
  threads as threadsApi,
  map as mapEndpoints,
  routes,
  atu,
} from '@/api/api-endpoints'
import { OBJECT } from '@/utils/map/types'
import { STATUSES } from '@/config/config'
import year from '@/utils/year'
import { OSMR_URL, osmrOptions } from '@/config/config'
import permissionMixin from '@/mixins/permission-mixin'
import sanitaryZoneMixin from '@/mixins/sanitary-zone-mixin'
import { PERMS } from '@/config/permissions'
import { COLORS } from '@/utils'

import DateSlider from '../geo/components/map-date-slider'
import ThreadsDrawer from '../geo/utils/ThreadsDrawer'
import OptimalModal from './components/OptimalModal'
import OptimalSchema from './components/OptimalSchema'
import OptimalZones from './components/OptimalZones'
import RoutesDrawer from '../routes/utils/RoutesDrawer'
import zoneMixin from './mixins/zone-mixin'

export default {
  name: 'SanitaryZone',
  components: {
    DateSlider,
    OptimalModal,
    OptimalSchema,
    OptimalZones,
  },
  mixins: [measureMixin, permissionMixin, zoneMixin, sanitaryZoneMixin],
  data: () => ({
    map: null,
    showMapLegend: true,
    year: year.getCurrentYear(),
    allowThreadTypes: [],
    threads: null,
    threadsDrawer: null,
    selectedMappingObject: '',
    ignoreStatuses: [
      ...STATUSES.map((item) => {
        return { ...item, isIgnore: false }
      }),
    ],
    showOptimalModal: false,
    showOptimalSchema: false,
    showOptimalZones: false,
    showOptimalText: false,
    optimalWaypoints: [],
    routesDrawer: null,
    routeLayer: null,
    optimalMarkerLayer: null,
    zones: {
      cost: null,
      control: null,
      collection: null,
      transportation: null,
      processing: null,
    },
  }),
  computed: {
    defaultZoom() {
      return REGION_CONFIG.map.zoom - 1
    },
    sliderYearsUrl() {
      return mapEndpoints.threadsYearDiapasone()
    },
    relations() {
      let threads = this?.threads?.data ? Object.values(this.threads?.data) : []
      if (!threads?.length) {
        return []
      }
      const nodes = []
      const tkoMinZoom = {}
      let threadData = this.$store.state.threadConstant.pointTypesColors
      let colorArr = []
      this.allowThreadTypes.forEach((el) => {
        colorArr.push(threadData[el])
      })
      threads = threads.filter((item) => {
        const status = this.ignoreStatuses.find(
          (el) => el.id === item.status_id && el.isIgnore
        )
        return !status
      })
      for (const thread of threads) {
        let tko_obj = thread.end
        if (!tko_obj) {
          continue
        }

        thread.status_id = thread.end.status_id

        let relationType = 'settlement-object'
        let showAfterZoom = this.defaultZoom //8
        let endTkoId = thread?.end.id
        let end = Object.assign(thread, {
          type: OBJECT.TKO,
          id: endTkoId,
          name: thread?.name || thread?.end.name,
          lat: thread?.lat || thread?.end.lat,
          lng: thread?.lng || thread?.end.lng,
          power: thread?.end.power,
          showAfterZoom,
        })
        const startType =
          thread.start.type === 'tko_object' ? OBJECT.TKO : OBJECT.CITY
        const start = Object.assign(
          {},
          {
            id: thread.start.id,
            lat: thread.start.lat,
            lng: thread.start.lng,
            type: startType,
            name: thread.start.name,
            showAfterZoom,
          }
        )

        // сообщение показ при наведение на связь
        let relationTooltip = ''
        relationTooltip += `${start.name} —> ${end.name}<br>`
        if (end.hasOwnProperty('garbage_value')) {
          relationTooltip += this.isBlank(end.garbage_value) + ' т/год '
        } else if (thread.hasOwnProperty('size_tonn')) {
          relationTooltip += thread.size_tonn + ' т/год '
        }

        if (end.hasOwnProperty('garbage_value_cubic')) {
          relationTooltip +=
            this.isBlank(end.garbage_value_cubic) + ' куб.м/год '
        } else if (end.hasOwnProperty('total_cubic')) {
          relationTooltip += this.isBlank(end.total_cubic) + ' куб.м/год '
        }

        let distance = 0
        let distanceInKM = 0

        if (
          !end.is_plan &&
          end.hasOwnProperty('distance') &&
          end.distance > 0
        ) {
          distance = end.distance
          distanceInKM = distance
        } else if (
          end.hasOwnProperty('planning_distance') &&
          end.planning_distance > 0
        ) {
          distance = end.planning_distance
        }

        if (distance) {
          distanceInKM = Math.round(distance / 1000)
          relationTooltip += distanceInKM + ' км '
        }

        end.relationTooltip = relationTooltip

        // для процент загрузки объекта нужен
        // power(мощность объекта) и garbage_sum(сумма всех отходов объекта)
        end.power = end.power || null
        end.garbage_sum = end.garbage_sum || null

        // создания индекса с минимальным масштабами
        // для отображения по id объекта
        if (!tkoMinZoom[endTkoId]) {
          tkoMinZoom[endTkoId] = +showAfterZoom
        }

        if (showAfterZoom < tkoMinZoom[endTkoId]) {
          tkoMinZoom[endTkoId] = +showAfterZoom
        }

        nodes.push({ start, end, relationType })
      }
      // обновления значений масштаба исходя из мин значений
      for (let i = 0; i < nodes.length; i++) {
        if (tkoMinZoom[nodes[i].end.id]) {
          nodes[i].end.showAfterZoom = tkoMinZoom[nodes[i].end.id]
        }
        if (nodes[i].relationType === 'object-object') {
          if (tkoMinZoom[nodes[i].start.id]) {
            nodes[i].start.showAfterZoom = tkoMinZoom[nodes[i].start.id]
          }
        }
        if (this.showThreadsAll) {
          nodes[i].end.oldShowAfterZoom = nodes[i].end.showAfterZoom
          nodes[i].end.showAfterZoom = 1
          nodes[i].start.oldShowAfterZoom = nodes[i].start.showAfterZoom
          nodes[i].start.showAfterZoom = 1
        }
      }
      return nodes
    },
  },
  methods: {
    registerOpenPopup() {},
    registerClosePopup() {},
    updateMapByYear(year) {
      this.year = year
    },
    async initMap() {
      this.map = L.map(this.$refs.map, {
        center: REGION_CONFIG.map.center,
        zoom: REGION_CONFIG.map.zoom,
        minZoom: REGION_CONFIG.map.minZoom,
        maxZoom: 18,
      })

      L.tileLayer(TILE_LAYER).addTo(this.map)

      this.borderDrawer = new BorderDrawer(this.map, this)
      this.borderDrawer.drawAll(REGION_CONFIG.polygon, [])
    },
    afterMapInit() {
      this.routesDrawer = new RoutesDrawer(this.map, this)
      this.threadsDrawer = new ThreadsDrawer(this.map, this)
    },
    async getThreads() {
      this.showOptimalText = false

      try {
        await this.$store.dispatch('createModel', {
          url: threadsApi.calculateModeling(),
          data: { year: this.year },
        })
        this.threads = await (
          await this.$store.dispatch('updateUrlEndpoint', {
            endpoint: mapEndpoints.threads({
              query: { year: this.year, is_model: 1 },
            }),
            fromCache: false,
          })
        ).data
        if (!this.threads.data.length) {
          this.$notify.warning({
            title: `Модельная схема потоков не рассчитана`,
          })
        }
        this.threadsDrawer.refreshMap(this.relations)
      } catch (e) {
        console.error(e)
        this.$notify.warning({
          title: `Модельная схема потоков не рассчитана`,
        })
      }
    },
    async saveOptimalRoute(waypoints) {
      this.$store.commit('preloader', true)

      this.optimalWaypoints = await (
        await this.$store.dispatch('createModel', {
          url: routes.optimize(),
          data: {
            waypoints,
          },
        })
      ).data.optimalWaypoints

      this.showOptimalModal = false
      this.routeLayer = L.featureGroup()
      this.routeLayer.addTo(this.map)
      const passedRoute = []
      this.optimalWaypoints.forEach((item) => {
        passedRoute.push([item[1], item[0]])
      })
      this.$notify.success({
        title: `Оптимальный маршрут рассчитан`,
      })
      const polyline = await this.getRightPolyline(passedRoute)
      const newRoute = []
      polyline.forEach((item) => {
        newRoute.push(L.latLng(item))
      })

      await this.routeLayer.addLayer(
        new L.polyline(newRoute, {
          color: 'red',
          weight: 3,
          opacity: 0.5,
          smoothFactor: 1,
        })
      )
      this.$store.commit('preloader', false)
    },
    saveOptimalSchema(coords) {
      this.showOptimalSchema = false
      if (this.optimalMarkerLayer) {
        this.map.removeLayer(this.optimalMarkerLayer)
      }
      if (!coords || !coords.length) {
        this.$notify.warning({
          title: `Оптимальное расположение объекта не рассчитано`,
        })
        return
      }

      const icon = L.icon({
        iconUrl: markerIcon,
        shadowUrl: iconShadow,
      })
      this.optimalMarkerLayer = L.marker([coords[1], coords[0]], {
        icon: icon,
      })
      this.$notify.success({
        title: `Оптимальное расположение объекта рассчитано`,
      })
      this.optimalMarkerLayer.addTo(this.map)
    },
    composeUrl(polyline) {
      return `${OSMR_URL}${polyline.join(';')}?${osmrOptions}`
    },
    async getRightPolyline(polyline) {
      const coordinates = await (
        await axios.get(this.composeUrl(polyline))
      ).data.trips[0].geometry.coordinates
      return coordinates.map((latLng) => [latLng[1], latLng[0]])
    },
    checkPermissions() {
      if (!this.userHavePermission(PERMS.VIEW)) {
        this.$router.push({
          name: 'geo',
        })
      }
    },
    getFlatCoords(object) {
      let polygon = JSON.parse(JSON.parse(object.polygon))
      polygon = polygon.flat()

      return polygon.map((first) => {
        if (first.length === 2) {
          return [first[1], first[0]]
        }
        first = first.map((el) => {
          if (Array.isArray(el)) {
            return [el[1], el[0]]
          }
          return el
        })
        return first
      })
    },
    async getObjects() {
      this.objects = await (
        await this.$store.dispatch('updateUrlEndpoint', {
          endpoint: atu.atuObjectUpdateOrCreate(),
        })
      ).data.data
      this.getBorders()
    },
    getBorders() {
      const mainBorderLayer = L.layerGroup()
      this.objects.map((border) => {
        if (!border.polygon) {
          return false
        }
        let polygon = this.getFlatCoords(border)

        const poly = L.polygon(polygon, {
          color: COLORS.red,
          fillColor: COLORS.red,
          fillOpacity: 0,
          weight: 2,
        })
        mainBorderLayer.addLayer(poly)
      })

      mainBorderLayer.addTo(this.map)
    },
    closeOptimalSchema() {
      this.getBorders()
      this.showOptimalSchema = false
    },
    onShowOptimalSchema() {
      this.showOptimalSchema = true
      this.showOptimalText = false
    },
    onShowOptimalModal() {
      this.showOptimalModal = true
      this.showOptimalText = false
    },
  },
  mounted() {
    this.checkPermissions()
    this.initMap()
    this.afterMapInit()
    this.initMeasure()
    this.getObjects()
    this.getZones()
  },
}
</script>
<style scoped lang="scss">
.modeling {
  &__button {
    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }

  &__text {
    div + div {
      margin-top: 5px;
    }
  }
}
</style>
